// OMNI Design System Typography Tokens for Chakra UI v3
// Mapped from omni_standard_css.css

export const omniTypography = {
  fonts: {
    // OMNI uses Open Sans as the primary font family
    body: {
      value:
        '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    },
    heading: {
      value:
        '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    },
    mono: {
      value:
        'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
  },

  fontSizes: {
    // OMNI size scale mapped to rem values
    '2xs': { value: '0.79rem' }, // --size-7: 11px
    xs: { value: '0.86rem' }, // --size-6: 12px
    sm: { value: '0.93rem' }, // --size-5: 13px
    md: { value: '1rem' }, // --size-4: 14px (normal)
    lg: { value: '1.14rem' }, // --size-3: 16px
    xl: { value: '1.29rem' }, // --size-2: 18px
    '2xl': { value: '1.5rem' }, // --size-1: 21px
    '3xl': { value: '1.75rem' }, // Extended for headings
    '4xl': { value: '2rem' }, // Extended for headings
    '5xl': { value: '2.25rem' }, // Extended for headings
    '6xl': { value: '2.5rem' }, // Extended for headings
    '7xl': { value: '3rem' }, // Extended for headings
    '8xl': { value: '3.5rem' }, // Extended for headings
    '9xl': { value: '4rem' }, // Extended for headings
  },

  fontWeights: {
    hairline: { value: 100 },
    thin: { value: 200 },
    light: { value: 300 },
    normal: { value: 400 }, // OMNI default body weight
    medium: { value: 500 },
    semibold: { value: 600 },
    bold: { value: 700 },
    extrabold: { value: 800 },
    black: { value: 900 },
  },

  lineHeights: {
    none: { value: 1 },
    tight: { value: 1.25 },
    snug: { value: 1.375 },
    normal: { value: 1.5 }, // OMNI default: --body-line-height: 1.5
    relaxed: { value: 1.625 },
    loose: { value: 2 },
  },

  letterSpacings: {
    tighter: { value: '-0.05em' },
    tight: { value: '-0.025em' },
    normal: { value: '0' },
    wide: { value: '0.025em' },
    wider: { value: '0.05em' },
    widest: { value: '0.1em' },
  },
};

// OMNI Text Styles for common patterns
export const omniTextStyles = {
  // Body text styles
  body: {
    default: {
      fontSize: 'md', // 14px (--size-4)
      fontWeight: 'normal',
      lineHeight: 'normal', // 1.5
      fontFamily: 'body',
    },
    small: {
      fontSize: 'xs', // 12px (--size-6)
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    large: {
      fontSize: 'lg', // 16px (--size-3)
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
  },

  // Heading styles
  heading: {
    h1: {
      fontSize: '4xl', // 2rem
      fontWeight: 'bold',
      lineHeight: 'tight',
      fontFamily: 'heading',
    },
    h2: {
      fontSize: '3xl', // 1.75rem
      fontWeight: 'bold',
      lineHeight: 'tight',
      fontFamily: 'heading',
    },
    h3: {
      fontSize: '2xl', // 1.5rem (--size-1)
      fontWeight: 'semibold',
      lineHeight: 'snug',
      fontFamily: 'heading',
    },
    h4: {
      fontSize: 'xl', // 1.29rem (--size-2)
      fontWeight: 'semibold',
      lineHeight: 'snug',
      fontFamily: 'heading',
    },
    h5: {
      fontSize: 'lg', // 1.14rem (--size-3)
      fontWeight: 'medium',
      lineHeight: 'normal',
      fontFamily: 'heading',
    },
    h6: {
      fontSize: 'md', // 1rem (--size-4)
      fontWeight: 'medium',
      lineHeight: 'normal',
      fontFamily: 'heading',
    },
  },

  // Table text styles
  table: {
    header: {
      fontSize: 'sm', // 13px (--size-5)
      fontWeight: 'semibold',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    cell: {
      fontSize: 'md', // 14px (--size-4)
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    caption: {
      fontSize: 'xs', // 12px (--size-6)
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
  },

  // Button text styles
  button: {
    large: {
      fontSize: 'lg', // 16px
      fontWeight: 'medium',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    medium: {
      fontSize: 'md', // 14px
      fontWeight: 'medium',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    small: {
      fontSize: 'sm', // 13px
      fontWeight: 'medium',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
  },

  // Form text styles
  form: {
    label: {
      fontSize: 'sm', // 13px
      fontWeight: 'medium',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    input: {
      fontSize: 'md', // 14px
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    helper: {
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
    error: {
      fontSize: 'xs', // 12px
      fontWeight: 'normal',
      lineHeight: 'normal',
      fontFamily: 'body',
    },
  },
};
